<template>
  <div class="main-layout">
    <div
      class="main-layout__header"
      :class="{'main-layout__header_fixed': isHeaderShrunk}"
    >
      <the-header
        :is-logo-collapsed="isNavbarCollapsed"
        :is-shrunk="isHeaderShrunk"
      />
    </div>
    <the-navbar class="main-layout__navbar"
                :class="{
                  'main-layout__navbar_fixed': isHeaderShrunk,
                  'main-layout__navbar_collapsed': isNavbarCollapsed
                }"
                :isCollapsed="isNavbarCollapsed"
                @open-cb-wizard="openCbWizard"
                @collapse-toggled="onCollapseToggled()"/>
    <div v-if="isAgentModeEnabled" class="main-layout__agent-mode__wrapper">
      <div ref="container"
           id="agent-mode-container"
           class="main-layout__content"
           :class="{'main-layout__content_grown-horizontally': isNavbarCollapsed}"
           @click="handleClick"
           @submit.prevent.stop="handleSubmit"
           v-html="htmlContent">
      </div>
      <div class="main-layout__agent-mode__loader" v-if="isDataUpdating">
        <base-loader/>
      </div>
    </div>
    <div v-else class="main-layout__content"
      :class="{'main-layout__content_grown-horizontally': isNavbarCollapsed}">
      <router-view></router-view>
    </div>
    <div class="main-layout__collapse-btn"
         :class="{
           'main-layout__collapse-btn_shrunk': isHeaderShrunk,
           'main-layout__collapse-btn_collapsed': isNavbarCollapsed
         }
         "
         @click="onCollapseToggled()">
      <div v-if="isNavbarCollapsed" class="main-layout__collapse-icon"
           :class="{'navbar__collapse-icon_turned': isNavbarCollapsed}">
        <div class="icon-two">
          <div class="hamburger"></div>
        </div>
      </div>
      <div
        v-else
        class="main-layout__arrow-icon"></div>
    </div>
    <modal name="cbWizard"
      v-bind="modalProps">
      <ContentBriefWizard @close="$modal.hide('cbWizard')" />
    </modal>
    <PaymentPlanManager v-if="shouldSelectPaymentPlan" />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import TheHeader from '@/common/TheHeader'
import TheNavbar from '@/common/TheNavbar'
import { cannotNavigateToRoute, accessToAgentRoutes } from './routeGuard'
import serialize from 'form-serialize'
import baseModalProps from '@/common/BaseModalProps'
import ContentBriefWizard from '@/features/content-briefs/content-brief-wizard'
import PaymentPlanManager from '@/features/agency/PaymentPlanManager'
const MIN_SIDEBAR_AVAILABLE_WIDTH = 1289 // 1024 (min screen width) + 265 (sidebar width)

/**
 * Component is used as host for Navbar, Header and other top-level components
 */
export default {
  name: 'MainLayout',
  components: {
    TheHeader,
    TheNavbar,
    ContentBriefWizard,
    PaymentPlanManager
  },
  data () {
    return {
      scrollTop: 0,
      requestUrl: '',
      isDataUpdating: false,
      isChatInited: false,
      skipLoading: false,
    }
  },
  mounted () {
    // TODO: uncomment to see the PaymentPlanPopup; remove this after testing
    document.addEventListener('scroll', () => {
      this.scrollTop = window.scrollY
    })
    window.addEventListener('resize', _.debounce(() => {
      this.onWindowResize()
    }, 250))
    this.onWindowResize()
    window.callHandleNewTabOpen = (e) => { this.handleNewTabOpen(e) }
    window.getCurrentRequestUrl = () => this.requestUrl
    window.resetFilters = () => { this.resetFilters() }
    window.showSpinner = (status) => { this.isDataUpdating = status }
    window.setAgencyModeHTML = (html) => { this.setHTML(html) }
  },
  computed: {
    ...mapState(['isAgentModeEnabled', 'adminRoutes', 'userRoles', 'unhashedClientId']),
    ...mapState('agency', ['shouldSelectPaymentPlan']),
    ...mapState('agencyModeData', ['html']),
    ...mapState('rankZoneCommon', ['trial']),
    modalProps () {
      const shareModalProps = {
      }
      return { ...baseModalProps, ...shareModalProps }
    },
    paymentPlanPopupProps () {
      return {
        ...baseModalProps,
        width: '920px',
        height: 'auto',
        scrollable: false,
      }
    },
    isHeaderShrunk () {
      return this.scrollTop > 0
    },
    htmlContent () {
      const acceptableUserRoles = ['admin', 'agency_user', 'agency_admin']
      return acceptableUserRoles.some(i => this.userRoles.includes(i)) ? this.html : ''
    }
  },
  methods: {
    ...mapActions(['toggleCollapsedStatus', 'changeAgencyMode', 'getAgencyModeRoutes', 'getUserRoles']),
    ...mapActions('agencyModeData', ['getHTML', 'setHTML', 'resetData', 'submitRequests']),
    openCbWizard() {
      this.$modal.show('cbWizard')
    },
    onCollapseToggled () {
      this.toggleCollapsedStatus(!this.isNavbarCollapsed)
    },
    // handling window resize, if width of user screen < minSidebarAvailableWidth then change way how to open sideBar
    onWindowResize () {
      if (!this.isNavbarCollapsed && document.body.clientWidth < MIN_SIDEBAR_AVAILABLE_WIDTH) {
        this.toggleCollapsedStatus(true)
      }
    },
    handleNewTabOpen (e) {
      if (e.url.startsWith('http://') || e.url.startsWith('https://')) {
        window.open(e.url, '_blank').focus()
        return
      }
      const tabName = e.url.split('/')[1].split('?')[0]
      const route = this.filteredAdminRoutes.find(i => {
        return i.children && i.children.some(route => route.name === tabName)
      })

      const redirectRoute = `${window.origin}/dashboard#${route.path}${e.url}`
      window.open(redirectRoute, '_blank').focus()
    },
    resetFilters () {
      const [clearUrl] = this.requestUrl.split('?')
      this.getData(clearUrl)
    },
    handleClick (e) {
      const skipRoles = ['tab', 'download', 'copy', 'blank', 'action']
      const targetRole = e.target.getAttribute('role')
      const parentRole = e.target.parentNode.getAttribute('role')
      if ([targetRole, parentRole].includes('prevent')) {
        e.preventDefault()
      } else if ((e.target.tagName.toLowerCase() === 'a' || e.target.parentNode.tagName.toLowerCase() === 'a') &&
        !skipRoles.includes(targetRole) &&
        !skipRoles.includes(parentRole)) {
        e.preventDefault()
        e.stopPropagation()
        const originLength = location.origin.length
        // next line receive url from url > remove origin > split to pathname & params in string
        const redirectUrl = (e.target.tagName.toLowerCase() === 'a' ? e.target.href : e.target.parentNode.href).substr(originLength)
        const [pathname, stringParams] = redirectUrl.split('?')
        const params = {}
        stringParams && stringParams.split('&').forEach(item => {
          const [key, value] = item.split('=')
          params[key] = value
        })
        const skipPaths = ['url']
        const parentUrl = this.filteredAdminRoutes.find(parent => {
          if (parent.children && parent.children.length) {
            return parent.children.some(child => !skipPaths.includes(child.path) ? redirectUrl.startsWith('/' + child.path) : false)
          }
          return false
        })
        if (parentUrl && this.$route.matched.length < 4) {
          this.$router.push({ path: parentUrl.path + redirectUrl })
        }
        this.getData(pathname, params)
      }
    },
    async handleSubmit (e) {
      const href = e.submitter.getAttribute('href')
      if (href) {
        window.location.href = window.location.origin + href.startsWith('/') ? href : '/' + href
        return
      }

      const form = e.path[0]
      const params = serialize(form, { hash: true })

      if (params.is_confirm && params.is_confirm === 'true') {
        const confirmed = confirm('Are you sure?')
        if (!confirmed) return
      }
      params.action = e.target.action.value || e.submitter.value
      const method = e.target.method
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params[key + '_copy'] = JSON.stringify(value)
        }
      })
      try {
        this.isDataUpdating = true
        this.requestUrl = await this.submitRequests({
          requestUrl: method.toLowerCase() === 'get' ? this.requestUrl.split('?')[0] : this.requestUrl,
          params,
          method
        })
      } finally {
        this.isDataUpdating = false
      }
    },
    async getData (pathname, params = {}, isShowSpinner = true) {
      const getRequestUrl = () => {
        if (pathname.startsWith(location.origin)) {
          return pathname
        } else {
          return window.location.origin + pathname
        }
      }
      const requestUrl = await getRequestUrl()
      try {
        isShowSpinner && (this.isDataUpdating = true)
        this.requestUrl = await this.getHTML({ requestUrl, method: 'GET', params })
      } catch (e) {
        console.log(e)
      } finally {
        isShowSpinner && (this.isDataUpdating = false)
      }
      // return for await functionality
      return true
    }
  },
  watch: {
    // handling changing user and check newUser access to currentRoute
    currentUser: {
      handler (newCurrentUser) {
        const currentRoute = this.$route.path
        if (cannotNavigateToRoute(newCurrentUser, currentRoute)) {
          this.$router.replace('/reports')
        }
        if (!this.isChatInited && newCurrentUser.first_name && newCurrentUser.last_name && newCurrentUser.email) {
          this.isChatInited = true
          if (Tawk_API.setAttributes && typeof Tawk_API.setAttributes === 'function') {
            Tawk_API.setAttributes({
              name: newCurrentUser.first_name + ' ' + newCurrentUser.last_name,
              email: newCurrentUser.email
            })
          }
        }
      },
      deep: true,
      immediate: true
    },
    // adminRoutes: {
    //   handler (newAdminRoutes) {
    //     if (accessToAgentRoutes(this.currentUser, newAdminRoutes, this.$route)) {
    //       this.$router.replace('/reports')
    //     }
    //   },
    //   deep: true,
    //   immediate: true
    // },
    requestUrl (value) {
      const skipUrls = {
        '/url_analyser': ['url']
      }
      const requestUrl = value.substr(window.location.origin.length)
      const currentSkipRequestUrl = skipUrls[requestUrl.split('?')[0]]
      const parentUrl = this.filteredAdminRoutes.find(parent => {
        if (parent.children && parent.children.length) {
          return parent.children.some(child => (!currentSkipRequestUrl || !currentSkipRequestUrl.includes(child.path)) && requestUrl.startsWith('/' + child.path))
        }
        return false
      })
      if (parentUrl && this.$route.matched.length < 4) {
        this.skipLoading = true
        this.$router.push({ path: parentUrl.path + requestUrl })
      }
    },
    async unhashedClientId () {
      try {
        const originLength = location.origin.length
        let pathName
        const urlParams = {}
        if (['edit', 'review', 'upload', 'children'].includes(this.$route.name)) {
          let routes = this.$route.path.split('/')
          routes.splice(0, 2)
          pathName = '/' + routes.join('/')
          Object.assign(urlParams, this.$route.query)
        } else if (this.requestUrl) {
          pathName = this.requestUrl.split('?')[0].substr(originLength)
        } else {
          const parentLength = this.$route.fullPath.split('/')[1].length + 1
          pathName = this.$route.fullPath.substr(parentLength)
        }

        this.isDataUpdating = true
        let requests

        if (this.isAgentModeEnabled) {
          requests = [
            this.getUserRoles(),
            this.getData(pathName, urlParams, false)
          ]
        } else {
          requests = [
            this.getUserRoles()
          ]
        }
        await Promise.all(requests).then(() => {
          if (this.isCurrentUserAgent) {
            this.getAgencyModeRoutes()
          }
        })
      } catch (e) {
        console.log(e)
      } finally {
        this.isDataUpdating = false
      }
    },
    '$route.name': {
      async handler (pathName) {
        if (pathName === 'skip_update') {
          return
        }

        const urlParams = {}
        const routesWithoutComponents = ['keyword_census', 'url_census', 'url', 'tag', 'url_analyser', 'url_attribute']
        const routesWithComponentsParents = {
          url: 'strategy'
        }

        const additionalRoutes = ['edit', 'upload', 'review', 'children']

        const isAgencyLink = !['url-planner', 'url_attribute', 'ClientRolesTab', 'UrlGeneralInfoTab', 'UrlRankReportTargetingTab', 'AssociatedURLAttributesTab'].includes(pathName) && routesWithoutComponents.some(i => {
          const url = routesWithComponentsParents[i] ? routesWithComponentsParents[i] + '/' + i : i
          return this.$route.fullPath.includes(url) && url !== 'strategy/url'
        })
        const requestUrl = !additionalRoutes.includes(pathName)
          ? '/' + pathName
          : '/' + this.$route.fullPath.split('/').slice(2).join('/')
        if (isAgencyLink && !this.isAgentModeEnabled) {
          this.changeAgencyMode(true)
          if (this.clientId) {
            this.$nextTick(async () => {
              await this.getData(requestUrl, urlParams)
            })
          }
        } else if (isAgencyLink && this.isAgentModeEnabled && this.clientId) {
          await this.getData(requestUrl, urlParams)
        } else {
          this.changeAgencyMode(false)
          this.resetData()
        }
      },
      immediate: true
    },
    html () {
      this.$nextTick(() => {
        if (!this.$refs.container) {
          return
        }
        const scripts = this.$refs.container.querySelectorAll('script:not([src])')
        for (const script of scripts) {
          // eslint-disable-next-line no-new-func
          const func = new Function(script.innerHTML)
          try {
            func()
          } catch (e) {
            console.error(e)
          }
        }
        try {
          const datepickers = $('[data-provide="datepicker-inline"]')
          if (datepickers && datepickers.length) {
            window.initDatePicker && window.initDatePicker($)
          }
        } catch (e) {
          console.error(e)
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
  .icon-two {
    position: absolute;
    top: 14px;
    left: 9px;
    cursor: pointer;
    color: currentColor;
  }

  .hamburger {
    position: absolute;
    border-radius: 1px;
    width: 12px;
    height: 2px;
    background: #000;
    transition: 0.5s;
  }

  .hamburger:before,
  .hamburger:after {
    position: absolute;
    border-radius: 1px;
    content: '';
    width: 12px;
    height: 2px;
    background: #000;
    transition: 0.5s;
  }

  .hamburger:before {
    top: -5px;
  }

  .hamburger:after {
    top: 5px;
  }

  .main-layout {
    overflow-y: hidden;
    background-color: $rfx-background-color;
    padding-bottom: $rfx-padding-bottom;

    &__header {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: $rfx-header-height-base;
      z-index: 10;

      &_fixed {
        height: $rfx-header-height-collapsed;
      }
    }

    &__arrow-icon {
      position: relative;
      display: flex;
      width: 30px;
      height: 30px;
      color: inherit;
      border-radius: 50%;
      border: solid 1px rgba(1, 22, 39, 0.1);
      background-color: $rfx-bright-turquoise;
      cursor: pointer;

      &::after {
        display: block;
        position: absolute;
        content: '';
        width: 9px;
        height: 9px;
        border-left: 2px solid #fff;
        border-bottom: 2px solid #fff;
        border-radius: 1px;
        transform: rotate(45deg);
        top: 9px;
        left: 11px;
      }
    }

    &__collapse-btn {
      position: fixed;
      top: 43px;
      left: 248px;
      z-index: 20;

      &_shrunk {
        top: 35px;
      }

      &_collapsed {
        left: 73px;
      }

      &_pushed-down {
        top: 43px + $rfx-trial-banner-height;
      }

      &:hover {
        color: $rfx-bright-blue;
      }
    }

    &__collapse-icon {
      position: relative;
      display: flex;
      width: 30px;
      height: 30px;
      color: inherit;
      border-radius: 50%;
      border: 1px solid $rfx-almost-white-darker;
      background-color: #fff;
      cursor: pointer;

      &_turned::before {
        transform: rotate(180deg);
        margin-left: 3px;
      }
    }

    &__navbar {
      position: fixed;
      top: 0;
      left: 0;
      width: $rfx-navbar-width-base;
      height: calc(100vh - #{$rfx-header-height-base});
      margin-top: $rfx-header-height-base;
      padding-bottom: $rfx-footer-height;
      z-index: 5;
      box-sizing: border-box;
      border-right: 1px solid $rfx-almost-white-darker;

      &_fixed {
        height: calc(100vh - #{$rfx-header-height-collapsed});
        margin-top: $rfx-header-height-collapsed;
      }

      &_collapsed {
        width: 90px;
        overflow-x: hidden;
        overflow-y: auto;
      }

      &_pushed-down {
        height: calc(100vh - #{$rfx-header-height-base + $rfx-trial-banner-height});
        margin-top: $rfx-header-height-base + $rfx-trial-banner-height;
      }
    }

    &__agent-mode {
      &__wrapper {
        position: relative;
      }

      &__loader {
        height: 100%;
        width: 100%;
        max-height: 100vh;
        position: fixed;
        z-index: 9999;
        top: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(255, 255, 255, .7);
      }
    }

    &__content {
      min-height: 100vh;
      background-color: $rfx-background-color !important;
      padding: $rfx-header-height-base 0 $rfx-footer-height $rfx-navbar-width-base;
      box-sizing: border-box;

      @media screen and (max-width: 1274px) {
        padding-left: 0;
      }

      &_grown {
        &-horizontally {
          padding-left: $rfx-navbar-width-collapsed!important;
        }
      }

      &_pushed-down {
        margin-top: $rfx-trial-banner-height;
      }
    }
  }
</style>
